import {
  CmdLabel,
  CmdLoader,
  CmdModal,
  CmdModalFooterButton,
} from '@commander-services/gui-components';
import * as React from 'react';
import { useIntl } from 'react-intl';
import { useSetRecoilState } from 'recoil';
import dayjs from 'dayjs';
import { IS_ACTIVE, IS_DISCARDED, IS_INACTIVE, IS_OK } from '../constants';
import { BluecoinRecord } from '../List/BluecoinsListState';
import ConfirmDialog from '../../ConfirmDialog';
import AssignmentForm from './AssignmentForm';
import PermissionsService, { PERMISSION_BLUECOINS } from '../../../Services/PermissionsService';
import LoaderService from '../../../Services/LoaderService';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { IOption } from '../../Forms/CmdField';
import { BluecoinAssignment, GetBluecoinApiResponse } from '../interfaces';
import {
  activate,
  closeAssignment,
  deactivate,
  discard,
  get,
  getCurrencies,
} from '../BluecoinsService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IBluecoinDetailProps {
  id: number | string;
  closeCallback: React.Dispatch<React.SetStateAction<boolean>>;
}

function BluecoinDetail(props: IBluecoinDetailProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const [data, setData] = React.useState<GetBluecoinApiResponse | null>(null);
  const [isCloseAssignmentConfirmDialogOpen, setIsCloseAssignmentConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isDiscardConfirmDialogOpen, setIsDiscardConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [isAddAssignmentConfirmDialogOpen, setIsAddAssignmentConfirmDialogOpen] =
    React.useState<boolean>(false);
  const [assignmentId, setAssignmentId] = React.useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = React.useState<boolean>(true);
  const [showLoader, setShowLoader] = React.useState<boolean>(true);
  const [showAssignmentForm, setShowAssignmentForm] = React.useState<boolean>(false);
  const [activationConfirm, setActivationConfirm] = React.useState<boolean>(false);
  const documentTitle = `${f({ id: 'bluecoin.modal.title' })} - ${f({ id: 'menu.bluecoins' })}`;
  useDocumentTitle(documentTitle);
  const [currencyOptions, setCurrencyOptions] = React.useState<IOption[]>([]);
  const { trackEvent } = useAnalytics();

  const getBluecoinCurrencies = async () => {
    const currencies = await getCurrencies();
    if (currencies) {
      setCurrencyOptions(currencies);
    }
  };

  const getData = async (afterAssignment?: boolean) => {
    const bluecoinData = await get(props.id);
    if (currencyOptions.length === 0) {
      getBluecoinCurrencies();
    }
    if (bluecoinData) {
      if (
        afterAssignment &&
        bluecoinData.bluecoin.state === IS_INACTIVE &&
        bluecoinData.assignments[0].toTs === null
      ) {
        setActivationConfirm(true);
      }
      setData(bluecoinData);
      setShowLoader(false);
    }
  };

  const handleActivate = async () => {
    trackEvent(['bluecoin form', 'click', 'Activate bluecoin']);
    LoaderService.showLoader(true);
    const response = await activate([props.id]);
    if (response && response.status === IS_OK) {
      setData((oldState: GetBluecoinApiResponse | null) => {
        if (oldState) {
          const newState = {
            ...oldState,
            bluecoin: {
              ...oldState.bluecoin,
              state: IS_ACTIVE,
            },
          };
          return newState;
        }
        return oldState;
      });
      LoaderService.showLoader(false);
    }
  };

  const handleDeactivate = async () => {
    trackEvent(['bluecoin form', 'click', 'Deactivate bluecoin']);
    LoaderService.showLoader(true);
    const response = await deactivate([props.id]);
    if (response && response.status === IS_OK) {
      // Update data in modal
      setData((oldState: GetBluecoinApiResponse | null) => {
        if (oldState) {
          const newState = {
            ...oldState,
            bluecoin: {
              ...oldState.bluecoin,
              state: IS_INACTIVE,
            },
          };
          return newState;
        }
        return oldState;
      });
      LoaderService.showLoader(false);
    }
  };

  const handleAddAssignment = (
    event:
      | React.MouseEvent<HTMLButtonElement, MouseEvent>
      | React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    event.preventDefault();
    trackEvent(['bluecoin form', 'click', 'Add assignment']);
    setShowAssignmentForm(true);
    if (isAddAssignmentConfirmDialogOpen) {
      setIsAddAssignmentConfirmDialogOpen(false);
    }
    setAssignmentId(null);
  };

  const handleEditAssignment = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const id = event.currentTarget.getAttribute('data-id');
    trackEvent(['bluecoin form', 'click', 'Edit assignment']);
    if (id) {
      setAssignmentId(Number(id));
      setShowAssignmentForm(true);
    }
  };

  const handleCloseAssignment = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();
    const id = event.currentTarget.getAttribute('data-id');
    trackEvent(['bluecoin form', 'click', 'Close assignment']);
    if (id) {
      setIsCloseAssignmentConfirmDialogOpen(true);
      setAssignmentId(Number(id));
    }
  };

  const handleCloseConfirmDialog = () => {
    setIsCloseAssignmentConfirmDialogOpen(false);
    trackEvent(['bluecoin form', 'click', 'Close assignment cancel']);
  };

  const handleConfirmCloseAssignment = async () => {
    if (assignmentId) {
      trackEvent(['bluecoin form', 'click', 'Close assignment confirm']);
      LoaderService.showLoader(true);
      const response = await closeAssignment(assignmentId);
      setData((oldState: GetBluecoinApiResponse | null) => {
        if (oldState && response) {
          const assigmnent = oldState.assignments.find(
            (item: BluecoinAssignment) => item.id === Number(assignmentId)
          );

          if (assigmnent) {
            const newAssignment: BluecoinAssignment = {
              ...assigmnent,
              ...response,
            };
            const newAssigments = oldState.assignments.map((item: BluecoinAssignment) => {
              if (item.id === newAssignment.id) {
                return newAssignment;
              }
              return item;
            });
            const newState: GetBluecoinApiResponse = {
              ...oldState,
              assignments: newAssigments,
            };
            return newState;
          }
          return oldState;
        }
        return oldState;
      });
      LoaderService.showLoader(false);
      setIsCloseAssignmentConfirmDialogOpen(false);
    }
  };

  const handleDiscard = async () => {
    if (data && data.bluecoin.id) {
      trackEvent(['bluecoin form', 'click', 'Discard bluecoin form']);
      LoaderService.showLoader(true);
      const response = await discard([Number(data.bluecoin.id)]);
      if (response && response.status === IS_OK) {
        getData();
        setIsDiscardConfirmDialogOpen(false);
      }
      LoaderService.showLoader(false);
    }
  };

  const handleDiscardConfirmDialog = async () => {
    if (data && data.bluecoin.id) {
      trackEvent(['bluecoin form', 'click', 'Discard bluecoin confirm dialog']);
      setIsDiscardConfirmDialogOpen(true);
    }
  };

  const handleCancelDiscard = () => {
    setIsDiscardConfirmDialogOpen(false);
    trackEvent(['bluecoin form', 'click', 'Cancel bluecoin discard']);
  };

  const handleConfirmAddAssignment = () => {
    setIsAddAssignmentConfirmDialogOpen(true);
    trackEvent(['bluecoin form', 'click', 'Add assignment confirm dialog']);
  };

  const handleCancelAddAssignmentConfirmDialog = () => {
    setIsAddAssignmentConfirmDialogOpen(false);
    trackEvent(['bluecoin form', 'click', 'Cancel add assignment confirm dialog']);
  };

  const buttons: CmdModalFooterButton[] = [
    {
      id: 'close-modal',
      type: 'button',
      className: 'e-button',
      title: f({ id: 'bluecoin.modal.button.close' }),
      closeCallback: props.closeCallback,
    },
  ];

  if (
    data &&
    PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
    data.bluecoin.state === IS_ACTIVE
  ) {
    buttons.push({
      id: 'deactivate-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.modal.button.deactivate' }),
      submitCallback: handleDeactivate,
    });
  }

  if (
    data &&
    PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
    data.bluecoin.state === IS_INACTIVE
  ) {
    buttons.push({
      id: 'activate-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.modal.button.activate' }),
      submitCallback: handleActivate,
    });
  }

  if (
    data &&
    PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
    (data.bluecoin.state === IS_INACTIVE || data.bluecoin.state === IS_ACTIVE)
  ) {
    buttons.push({
      id: 'discard-button',
      type: 'submit',
      className: 'e-button e-button--gray',
      title: f({ id: 'bluecoin.modal.button.discard' }),
      submitCallback: handleDiscardConfirmDialog,
    });
  }

  React.useEffect(() => {
    getData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reloadDetail = () => {
    setShowLoader(true);
    getData(true);
  };

  if (showAssignmentForm) {
    return (
      <AssignmentForm
        id={assignmentId}
        bluecoinId={props.id}
        closeCallback={setShowAssignmentForm}
        // updateBluecoinCallback={setData}
        reloadCallback={reloadDetail}
        currencies={currencyOptions || []}
      />
    );
  }

  const getCurrencyName = (id: number) => {
    if (!id) {
      return '';
    }
    const currency = currencyOptions.find((i) => String(i.value) === String(id));
    return currency?.item;
  };

  const handleCancelActivationConfirm = () => {
    setActivationConfirm(false);
  };

  const handleActivation = () => {
    handleActivate();
    setActivationConfirm(false);
  };

  return (
    <CmdModal
      id="bluecoin-detail"
      title={f({ id: 'bluecoin.modal.title' })}
      footerButtons={buttons}
      usePortal={true}
      width="940px"
    >
      {data && data.bluecoin && isDetailModalOpen && (
        <>
          <CmdLabel
            id="uuid"
            title={f({ id: 'bluecoin.detail.uuid' })}
            value={data.bluecoin.uuid}
            areLabels={true}
          />
          <CmdLabel
            id="macAdress"
            title={f({ id: 'bluecoin.detail.macAddress' })}
            value={data.bluecoin.macAddress}
            areLabels={true}
          />
          <CmdLabel
            id="serialNumber"
            title={f({ id: 'bluecoin.detail.serialNumber' })}
            value={data.bluecoin.serialNumber}
            areLabels={true}
          />
          <CmdLabel
            id="manufacturer"
            title={f({ id: 'bluecoin.detail.manufacturer' })}
            value={data.bluecoin.manufacturer}
            areLabels={true}
          />
          <CmdLabel
            id="model"
            title={f({ id: 'bluecoin.detail.model' })}
            value={data.bluecoin.model}
            areLabels={true}
          />
          <CmdLabel
            id="settingsSignalStrength"
            title={f({ id: 'bluecoin.detail.settingsSignalStrength' })}
            value={
              data.bluecoin.settingSignalStrength ? String(data.bluecoin.settingSignalStrength) : ''
            }
            areLabels={true}
          />
          <CmdLabel
            id="state"
            title={f({ id: 'bluecoin.detail.state' })}
            value={f({ id: `bluecoin.detail.state.${data.bluecoin.state}` })}
            areLabels={true}
          />
          <CmdLabel
            id="customer"
            title={f({ id: 'bluecoin.detail.customer' })}
            value={data.customers
              .map((customer: { id: number; name: string; label: string }) => customer.name)
              .join(', ')}
            areLabels={true}
          />
          <hr />
          <h4>{f({ id: 'bluecoin.assignments' })}</h4>
          {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
            (data.bluecoin.state === IS_ACTIVE || data.bluecoin.state === IS_INACTIVE) && (
              <>
                <button
                  className="e-button e-button--gray"
                  style={{ marginBottom: '1rem' }}
                  onClick={
                    data.assignments[0] && data.assignments[0].toTs === null
                      ? handleConfirmAddAssignment
                      : handleAddAssignment
                  }
                  data-cy="bluecoin-assignment-add"
                >
                  {f({ id: 'bluecoin.assignment.add' })}
                </button>
                <br />
              </>
            )}
          <div
            className="table-responsive"
            style={{ minHeight: 'auto', overflow: 'auto', maxHeight: '200px' }}
          >
            <table className="table table-striped table-bordered table-listings m-0">
              <thead>
                <tr>
                  <th className="th-sticky-top w-sm">{f({ id: 'bluecoin.assignment.name' })}</th>
                  <th className="th-sticky-top w-sm">{f({ id: 'bluecoin.assignment.notes' })}</th>
                  <th className="th-sticky-top w-sm">
                    {f({ id: 'bluecoin.assignment.description' })}
                  </th>
                  <th className="th-sticky-top w-sm">
                    {f({ id: 'bluecoin.assignment.unitPrice' })}
                  </th>
                  <th className="th-sticky-top w-sm">
                    {f({ id: 'bluecoin.assignment.assignFrom' })}
                  </th>
                  <th className="th-sticky-top w-sm">
                    {f({ id: 'bluecoin.assignment.assignTo' })}
                  </th>
                  <th className="th-sticky-top w-sm text-center">
                    {f({ id: 'table.actionsBtn' })}
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.assignments.map((item: BluecoinAssignment) => (
                  <tr key={`assignment-item-${item.id}-${data.bluecoin.id}`}>
                    <td className="align-middle">{item.deviceName}</td>
                    <td className="align-middle">{item.deviceNotes}</td>
                    <td className="align-middle">{item.deviceDescription}</td>
                    <td className="align-middle">
                      {item.unitPrice} {getCurrencyName(item.unitCurrency)}
                    </td>
                    <td className="align-middle">
                      {dayjs(item.fromTs).format('DD.MM.YYYY HH:mm')}
                    </td>
                    <td className="align-middle">
                      {item.toTs && dayjs(item.toTs).format('DD.MM.YYYY HH:mm')}
                    </td>
                    <td className="text-center">
                      {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) && (
                        <a
                          href="#"
                          className="px-1 py-xs text-decoration-none text-gray"
                          title={f({ id: 'bluecoin.assignment.action.edit' })}
                          onClick={handleEditAssignment}
                          data-id={item.id}
                          data-cy={`bluecoin-edit-${item.id}`}
                        >
                          <i className="cmd-icon cmd-icon-edit" />
                          <span className="sr-only">
                            {f({ id: 'bluecoin.assignment.action.edit' })}
                          </span>
                        </a>
                      )}
                      {/* {item.toTs && (
                        <a
                          href="#"
                          className="px-1 py-xs text-decoration-none text-gray"
                          onClick={handleOpenAssignment}
                          title={f({ id: 'bluecoin.assignment.action.reopen' })}
                          data-id={item.id}
                        >
                          <i className="cmd-icon cmd-icon-check" />
                        </a>
                      )} */}
                      {PermissionsService.hasAccess(PERMISSION_BLUECOINS, ['update']) &&
                        !item.toTs && (
                          <a
                            href="#"
                            className="px-1 py-xs text-decoration-none text-gray"
                            onClick={handleCloseAssignment}
                            title={f({ id: 'bluecoin.assignment.action.close' })}
                            data-id={item.id}
                            data-cy={`bluecoin-remove-${item.id}`}
                          >
                            <i className="cmd-icon cmd-icon-bin" />
                            <span className="sr-only">
                              {f({ id: 'bluecoin.assignment.action.close' })}
                            </span>
                          </a>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {showLoader && <CmdLoader inline={true} />}
      {isCloseAssignmentConfirmDialogOpen && (
        <ConfirmDialog
          title={f({ id: 'bluecoin.confirmDialog.close.title' })}
          cancelCallback={handleCloseConfirmDialog}
          continueCallback={handleConfirmCloseAssignment}
          cancelButtonTitle={f({ id: 'bluecoin.confirmDialog.close.button.cancel' })}
          continueButtonTitle={f({ id: 'bluecoin.confirmDialog.close.button.continue' })}
        >
          {f({ id: 'bluecoin.confirmDialog.close.text' })}
        </ConfirmDialog>
      )}
      {isDiscardConfirmDialogOpen && (
        <ConfirmDialog
          title={f({ id: 'bluecoin.confirmDialog.discard.title' })}
          cancelCallback={handleCancelDiscard}
          continueCallback={handleDiscard}
          cancelButtonTitle={f({ id: 'bluecoin.confirmDialog.discard.button.cancel' })}
          continueButtonTitle={f({ id: 'bluecoin.confirmDialog.discard.button.continue' })}
        >
          {f({ id: 'bluecoin.confirmDialog.discard.text' })}
        </ConfirmDialog>
      )}
      {isAddAssignmentConfirmDialogOpen && (
        <ConfirmDialog
          title={f({ id: 'bluecoin.confirmDialog.addAssignment.title' })}
          cancelCallback={handleCancelAddAssignmentConfirmDialog}
          continueCallback={handleAddAssignment}
          continueButtonTitle={f({ id: 'bluecoin.confirmDialog.addAssignment.button.continue' })}
          cancelButtonTitle={f({ id: 'bluecoin.confirmDialog.addAssignment.button.cancel' })}
        >
          {f({ id: 'bluecoin.confirmDialog.addAssignment.text' })}
        </ConfirmDialog>
      )}
      {activationConfirm && (
        <ConfirmDialog
          title={f({ id: 'bluecoin.confirmDialog.addAssignment.title' })}
          cancelCallback={handleCancelActivationConfirm}
          continueCallback={handleActivation}
          continueButtonTitle={f({ id: 'expenseTypeForm.active.yes' })}
          cancelButtonTitle={f({ id: 'bluecoin.confirmDialog.addAssignment.button.cancel' })}
        >
          {f({ id: 'bluecoin.confirmDialog.activation.text' })}
        </ConfirmDialog>
      )}
    </CmdModal>
  );
}

export default BluecoinDetail;
